import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check, FolderOpen, Image as ImageIcon, ImageOff, LoaderCircle, Maximize2,
  Music2, Plus, Trash2, Upload, Video, X,
} from 'lucide-react';
import { api } from '../lib/api.js';
import CanvasMaterialSaveDialog from './CanvasMaterialSaveDialog.jsx';

const FALLBACK_COLLECTIONS = [
  { id: 'person', name: '人物' },
  { id: 'scene', name: '场景' },
  { id: 'prop', name: '道具' },
];
const MEDIA_TABS = [
  { id: 'image', label: '图片', Icon: ImageIcon },
  { id: 'video', label: '视频', Icon: Video },
  { id: 'audio', label: '音频', Icon: Music2 },
];
const ACCEPT_BY_KIND = {
  image: 'image/jpeg,image/png,image/webp',
  video: 'video/mp4,video/webm,video/quicktime',
  audio: 'audio/mpeg,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,audio/aac,audio/ogg',
};

function materialKind(material) {
  if (['image', 'video', 'audio'].includes(material?.mediaType)) return material.mediaType;
  if (String(material?.mimeType || '').startsWith('video/')) return 'video';
  if (String(material?.mimeType || '').startsWith('audio/')) return 'audio';
  return 'image';
}

function materialRatio(material, fallback = '16 / 9') {
  const explicit = String(material?.ratio || material?.aspectRatio || '').trim();
  if (/^\d+(?:\.\d+)?:\d+(?:\.\d+)?$/.test(explicit)) return explicit.replace(':', ' / ');
  const width = Number(material?.width || 0);
  const height = Number(material?.height || 0);
  return width > 0 && height > 0 ? `${width} / ${height}` : fallback;
}

export default function CanvasSettingsPanel({
  materials = [], project, onAdd, onClose, onOpenLibrary, onPreview, onRefresh, onNotify, onUploadStateChange,
}) {
  const collections = project?.materialCollections?.length ? project.materialCollections : FALLBACK_COLLECTIONS;
  const [mediaType, setMediaType] = useState('image');
  const [category, setCategory] = useState(collections[0]?.id || 'person');
  const [uploading, setUploading] = useState(false);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [addingCollection, setAddingCollection] = useState(false);
  const [collectionName, setCollectionName] = useState('');
  const [collectionBusy, setCollectionBusy] = useState(false);
  const [deleteArmed, setDeleteArmed] = useState('');
  const inputRef = useRef(null);
  const projectMaterials = useMemo(
    () => materials.filter((item) => item.projectId === project?.id),
    [materials, project?.id],
  );
  const visible = projectMaterials.filter((item) => (
    materialKind(item) === mediaType && (mediaType !== 'image' || item.category === category)
  ));

  useEffect(() => {
    if (!collections.some((item) => item.id === category)) setCategory(collections[0]?.id || 'person');
  }, [category, collections]);

  useEffect(() => {
    onUploadStateChange?.(Boolean(pendingUpload));
    return () => onUploadStateChange?.(false);
  }, [onUploadStateChange, pendingUpload]);

  useEffect(() => {
    if (!deleteArmed) return undefined;
    const close = (event) => {
      if (event.key === 'Escape' || (event.type === 'pointerdown' && !event.target.closest?.('.canvas-settings-tab, .canvas-setting-item'))) setDeleteArmed('');
    };
    document.addEventListener('pointerdown', close, true);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('pointerdown', close, true);
      document.removeEventListener('keydown', close);
    };
  }, [deleteArmed]);

  const closeUploadDialog = () => {
    if (pendingUpload?.objectUrl) URL.revokeObjectURL(pendingUpload.objectUrl);
    setPendingUpload(null);
  };

  const chooseUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const kind = file.type.startsWith('video/') ? 'video' : file.type.startsWith('audio/') ? 'audio' : 'image';
    const objectUrl = URL.createObjectURL(file);
    setPendingUpload({
      file, kind, mediaType: kind, objectUrl, url: objectUrl, previewUrl: kind === 'image' ? objectUrl : '',
      name: file.name.replace(/\.[^.]+$/u, '').slice(0, 100) || `${MEDIA_TABS.find((item) => item.id === kind)?.label || ''}素材`,
    });
  };

  const upload = async (draft) => {
    setUploading(true);
    try {
      await api.createMaterial({
        projectId: project.id, category: draft.category || collections[0]?.id || 'person',
        mediaType: draft.mediaType || draft.kind, name: draft.name, file: draft.file, source: 'canvas',
      });
      closeUploadDialog();
      await onRefresh?.();
      setMediaType(draft.mediaType || draft.kind || 'image');
      if (draft.mediaType === 'image') setCategory(draft.category);
      onNotify?.('素材已加入收藏夹');
    } catch (error) {
      onNotify?.(error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const createCollection = async (event) => {
    event.preventDefault();
    const name = collectionName.trim();
    if (!name || collectionBusy) return;
    setCollectionBusy(true);
    try {
      const result = await api.createMaterialCollection(project.id, name);
      await onRefresh?.();
      setCategory(result.collection.id);
      setCollectionName('');
      setAddingCollection(false);
      onNotify?.('图片收藏分组已创建');
    } catch (error) {
      onNotify?.(error.message, 'error');
    } finally {
      setCollectionBusy(false);
    }
  };

  const deleteCollection = async (collectionId) => {
    if (deleteArmed !== `collection:${collectionId}`) return setDeleteArmed(`collection:${collectionId}`);
    setCollectionBusy(true);
    try {
      const result = await api.deleteMaterialCollection(project.id, collectionId);
      setCategory(result.fallbackCollection?.id || '');
      setDeleteArmed('');
      await onRefresh?.();
      onNotify?.(result.moved ? `分组已删除，${result.moved} 个素材已迁移` : '图片收藏分组已删除');
    } catch (error) {
      onNotify?.(error.message, 'error');
    } finally {
      setCollectionBusy(false);
    }
  };

  const deleteMaterial = async (material) => {
    if (deleteArmed !== material.id) return setDeleteArmed(material.id);
    try {
      await api.deleteMaterial(material.id);
      setDeleteArmed('');
      await onRefresh?.();
      onNotify?.('收藏素材已删除');
    } catch (error) {
      onNotify?.(error.message, 'error');
    }
  };

  const addMaterial = (material) => {
    onAdd?.(material);
    if (materialKind(material) === 'image') onClose?.();
  };

  const dragMaterial = (event, material) => {
    event.dataTransfer.effectAllowed = 'copy';
    event.dataTransfer.setData('application/x-nebula-favorite', JSON.stringify(material));
  };

  return <>
    <aside className="canvas-settings-panel" aria-label="收藏夹">
      <header>
        <div className="canvas-settings-heading">
          <div><strong>收藏夹</strong>{mediaType === 'image' && <button type="button" onClick={() => setAddingCollection((value) => !value)} title="新建图片分组"><Plus size={14} /></button>}</div>
          <small>点击或拖入画布快速使用</small>
        </div>
        <div className="canvas-settings-actions">
          <span>{projectMaterials.length}</span>
          <button type="button" onClick={onClose} aria-label="关闭收藏夹"><X size={16} /></button>
        </div>
      </header>
      <div className="canvas-settings-media-tabs" role="tablist">
        {MEDIA_TABS.map(({ id, label, Icon }) => <button type="button" role="tab" aria-selected={mediaType === id} className={mediaType === id ? 'is-active' : ''} key={id} onClick={() => { setMediaType(id); setDeleteArmed(''); }}>
          <Icon size={15} /><span>{label}</span><b>{projectMaterials.filter((item) => materialKind(item) === id).length}</b>
        </button>)}
      </div>
      {mediaType === 'image' && <>
        {addingCollection && <form className="canvas-settings-new-collection" onSubmit={createCollection}>
          <input autoFocus maxLength={30} value={collectionName} onChange={(event) => setCollectionName(event.target.value)} placeholder="输入图片分组名称" />
          <button type="submit" disabled={!collectionName.trim() || collectionBusy} title="创建分组">{collectionBusy ? <LoaderCircle className="spin" size={14} /> : <Check size={14} />}</button>
          <button type="button" onClick={() => { setAddingCollection(false); setCollectionName(''); }} title="取消"><X size={14} /></button>
        </form>}
        <div className="canvas-settings-tabs" role="tablist">
          {collections.map((item) => <div className={`canvas-settings-tab ${category === item.id ? 'is-active' : ''}`} key={item.id}>
            <button type="button" role="tab" aria-selected={category === item.id} onClick={() => { setCategory(item.id); setDeleteArmed(''); }}>
              <span>{item.name}</span><b>{projectMaterials.filter((material) => materialKind(material) === 'image' && material.category === item.id).length}</b>
            </button>
            {deleteArmed === `collection:${item.id}` ? <span className="canvas-settings-tab-confirm"><button type="button" onClick={() => void deleteCollection(item.id)} disabled={collectionBusy}>删除</button><button type="button" onClick={() => setDeleteArmed('')} disabled={collectionBusy}>取消</button></span> : <button type="button" className="canvas-settings-tab-delete" onClick={() => void deleteCollection(item.id)} disabled={collectionBusy} title={`删除${item.name}分组`}><X size={11} /></button>}
          </div>)}
        </div>
      </>}
      <div className="canvas-settings-grid">
        {visible.map((material) => {
          const kind = materialKind(material);
          return <article className={`canvas-setting-item kind-${kind}`} key={material.id} draggable onDragStart={(event) => dragMaterial(event, material)}>
            <div className="canvas-setting-preview" style={{ aspectRatio: materialRatio(material, kind === 'video' ? '16 / 9' : '16 / 9') }}>
              <button type="button" className="canvas-setting-add" onClick={() => addMaterial(material)} title={`添加 ${material.name} 到画布`}>
                {kind === 'image' && (material.previewUrl || material.url) && <img src={material.previewUrl || material.url} alt="" loading="lazy" decoding="async" />}
                {kind === 'video' && <video src={material.url} poster={material.poster || undefined} preload="metadata" muted />}
                {kind === 'audio' && <span className="canvas-setting-audio"><Music2 size={28} /><audio src={material.url} controls preload="metadata" /></span>}
                {!material.url && <ImageOff size={20} />}
              </button>
            </div>
            <div className="canvas-setting-card-actions">
              <button type="button" onClick={() => addMaterial(material)} title="加入画布"><Plus size={14} /></button>
              <button type="button" onClick={() => onPreview?.({ kind, url: material.url, downloadUrl: material.downloadUrl, name: material.name })} title="展开预览"><Maximize2 size={14} /></button>
              <button type="button" className={`is-danger ${deleteArmed === material.id ? 'is-armed' : ''}`} onClick={() => void deleteMaterial(material)} title={deleteArmed === material.id ? '再次点击确认删除' : '删除收藏'}>
                {deleteArmed === material.id ? <Check size={14} /> : <Trash2 size={14} />}
              </button>
            </div>
            <strong title={material.name}>{material.name}</strong>
          </article>;
        })}
        {!visible.length && <button type="button" className="canvas-settings-empty" onClick={() => inputRef.current?.click()}>
          <Upload size={20} /><strong>收藏夹中还没有{MEDIA_TABS.find((item) => item.id === mediaType)?.label}</strong>
        </button>}
      </div>
      <footer>
        <input ref={inputRef} type="file" accept={ACCEPT_BY_KIND[mediaType]} hidden onChange={chooseUpload} />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}><Upload size={15} />上传{MEDIA_TABS.find((item) => item.id === mediaType)?.label}</button>
        <button type="button" onClick={onOpenLibrary}><FolderOpen size={15} />历史素材库</button>
      </footer>
    </aside>
    <CanvasMaterialSaveDialog source={pendingUpload} collections={collections} initialCollectionId={category} title="加入收藏夹" submitLabel="收藏" busy={uploading} onClose={closeUploadDialog} onSubmit={upload} />
  </>;
}
